/*
 * Chunked file upload helper.
 *
 * For files larger than ~30 MB the single multipart POST tends to be
 * blocked by platform proxies (Kubernetes nginx-ingress typically caps at
 * ~50-60 MB). This helper splits the file into small chunks and uploads
 * them one by one. Server reassembles them on finalize.
 *
 * Usage:
 *   const result = await uploadInChunks(file, "feed", (p) => setProgress(p))
 *   // result = { url, media_type, content_type, file_name, size }
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DIRECT_LIMIT = 20 * 1024 * 1024; // Use direct upload for <= 20 MB
const DEFAULT_CHUNK = 5 * 1024 * 1024; // 5 MB
const PARALLEL_CHUNKS = 3;

export async function uploadInChunks(file, target, onProgress) {
  // Fast path — small file goes through legacy single-shot endpoint
  if (file.size <= DIRECT_LIMIT) {
    return directUpload(file, target, onProgress);
  }

  // 1. Init
  const { data: initRes } = await axios.post(
    `${API}/uploads/init`,
    {
      filename: file.name,
      content_type: file.type || "application/octet-stream",
      size: file.size,
      target,
    },
    { withCredentials: true }
  );
  const uploadId = initRes.upload_id;
  const chunkSize = Math.min(initRes.chunk_size || DEFAULT_CHUNK, DEFAULT_CHUNK);
  const total = Math.ceil(file.size / chunkSize);

  try {
    // 2. Upload chunks in parallel (3 at a time) with retries — much faster on mobile
    const sendChunk = async (i) => {
      const start = i * chunkSize;
      const chunk = file.slice(start, Math.min(start + chunkSize, file.size));
      for (let attempt = 0; ; attempt++) {
        try {
          const form = new FormData();
          form.append("file", chunk, `chunk-${i}`);
          await axios.post(`${API}/uploads/chunk/${uploadId}/${i}`, form, {
            withCredentials: true,
            headers: { "Content-Type": "multipart/form-data" },
          });
          return;
        } catch (e) {
          if (attempt >= 2) throw e;
          await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
        }
      }
    };
    let next = 0;
    let done = 0;
    const worker = async () => {
      while (next < total) {
        const i = next++;
        await sendChunk(i);
        done++;
        if (onProgress) onProgress(done / total);
      }
    };
    await Promise.all(Array.from({ length: Math.min(PARALLEL_CHUNKS, total) }, worker));

    // 3. Finalize — server reassembles and stores
    const { data: finalRes } = await axios.post(
      `${API}/uploads/finalize/${uploadId}`,
      {},
      { withCredentials: true }
    );
    return finalRes;
  } catch (err) {
    // Best-effort abort
    try {
      await axios.delete(`${API}/uploads/${uploadId}`, { withCredentials: true });
    } catch {
      /* ignore */
    }
    throw err;
  }
}

async function directUpload(file, target, onProgress) {
  const form = new FormData();
  form.append("file", file);
  // Pick legacy endpoint per target
  let url;
  if (target === "feed") url = `${API}/feed/upload`;
  else if (target === "avatar") url = `${API}/users/me/avatar`;
  else if (target.startsWith("room:"))
    url = `${API}/rooms/${target.slice(5)}/upload`;
  else url = `${API}/feed/upload`;

  const { data } = await axios.post(url, form, {
    withCredentials: true,
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: onProgress
      ? (e) => {
          if (e.total) onProgress(e.loaded / e.total);
        }
      : undefined,
  });

  // Normalize response shape to match finalize()
  const ct = data.content_type || file.type || "";
  const media_type = ct.startsWith("image/")
    ? "image"
    : ct.startsWith("video/")
      ? "video"
      : ct.startsWith("audio/")
        ? "audio"
        : "file";
  return {
    url: data.url,
    content_type: ct,
    media_type,
    file_name: data.file_name || file.name,
    size: file.size,
  };
}
